import React from "react";
import { InventoryParameter } from "../../types/Inventory";
import { CategoryParameter, ParameterDataType, SelectionType } from "../../types/Category";
import { Unit } from "../../types/Unit";
import MultiSelectDropdown from "../filters/MultiSelectDropdown";

interface Props {
    parameters: CategoryParameter[];
    units?: Unit[];
    /** All currently filled-in values, keyed by parameter name. */
    values: Record<string, InventoryParameter>;
    /** Reports a changed value; null clears it (parameter will be skipped). */
    onChange: (name: string, value: InventoryParameter | null) => void;
    disabled?: boolean;
    /** Rendered between the "All optional" note and the parameter rows
     *  (the why-page demo puts its try-the-dropdowns nudge there). */
    headerExtra?: React.ReactNode;
    /** Hides the "All optional" note (demo pages where saving isn't a thing). */
    hideNote?: boolean;
}

/**
 * All the category's parameters as one form, filled in while creating a
 * listing (same idea as the search form) instead of adding them one by one
 * after the first save. Everything is optional — untouched rows are simply
 * not added. The collected values go through the normal AddParameter patch
 * right after the create call, so validation stays server-side.
 */
const CreateParametersForm: React.FC<Props> = ({ parameters, units = [], values, onChange, disabled, headerExtra, hideNote }) => {

    const allowedUnitsFor = (meta: CategoryParameter): string[] => {
        const raw = meta.unitIds ?? (meta.unitId ? [meta.unitId] : []);
        if (meta.convertible === false && raw[0]) {
            const dimId = units.find(u => u.id === raw[0])?.unitDimensionId;
            if (dimId) return units.filter(u => u.unitDimensionId === dimId).map(u => u.id);
        }
        return raw;
    };

    const pickDefaultUnit = (ids: string[]): string =>
        ids.find(id => units.find(u => u.id === id)?.name?.toUpperCase() === "USD") ?? ids[0] ?? "";

    const unitName = (uid?: string) =>
        uid ? (units.find(u => u.id === uid)?.name ?? uid) : "";

    /** A draft carrying the metadata the AddParameter payload needs. */
    const draftFor = (meta: CategoryParameter, value: any, unit?: string): InventoryParameter => ({
        id: meta.id,
        name: meta.name,
        dataType: meta.dataType,
        value,
        ...(meta.dataType === ParameterDataType.SELECTABLE
            ? { selectionType: (meta as any).selectionType ?? SelectionType.SINGLE }
            : {}),
        ...(meta.dataType === ParameterDataType.DOUBLE
            ? { units: unit ?? values[meta.name]?.units ?? pickDefaultUnit(allowedUnitsFor(meta)) }
            : {}),
        ...(meta.dataType === ParameterDataType.INTEGER
            ? { units: meta.unitId ?? "" }
            : {}),
    });

    const rowFor = (meta: CategoryParameter) => {
        const current = values[meta.name];
        const isDouble = meta.dataType === ParameterDataType.DOUBLE;
        const allowedUnits = isDouble ? allowedUnitsFor(meta) : [];
        const selectionType = ((meta as any).selectionType as SelectionType) ?? SelectionType.SINGLE;
        const options: string[] = ((meta as any).options as string[]) ?? [];

        if (meta.dataType === ParameterDataType.BOOLEAN) {
            // Three-way: an unticked box can't tell "no" from "don't say".
            const sel = current === undefined ? "" : current.value ? "yes" : "no";
            return (
                <select
                    className="bh-input"
                    value={sel}
                    onChange={(e) => onChange(meta.name,
                        e.target.value === "" ? null : draftFor(meta, e.target.value === "yes"))}
                    disabled={disabled}
                    style={{ width: "auto" }}
                >
                    <option value="">— not set —</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                </select>
            );
        }

        if (meta.dataType === ParameterDataType.SELECTABLE) {
            if (options.length === 0) {
                return <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>No options defined.</span>;
            }
            if (selectionType === SelectionType.SINGLE) {
                return (
                    <select
                        className="bh-input"
                        value={(current?.value as string) ?? ""}
                        onChange={(e) => onChange(meta.name,
                            e.target.value === "" ? null : draftFor(meta, e.target.value))}
                        disabled={disabled}
                        style={{ width: "auto" }}
                    >
                        <option value="">— not set —</option>
                        {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                );
            }
            // MULTI: the same checkbox-panel dropdown (with removable chips
            // underneath) that the search form uses.
            const selected: string[] = Array.isArray(current?.value) ? current!.value : [];
            return (
                <MultiSelectDropdown
                    options={options}
                    values={selected}
                    onChange={(next) => onChange(meta.name, next.length ? draftFor(meta, next) : null)}
                    disabled={disabled}
                />
            );
        }

        if (meta.dataType === ParameterDataType.DATE) {
            return (
                <input
                    className="bh-input"
                    type="date"
                    value={(current?.value as string) ?? ""}
                    onChange={(e) => onChange(meta.name,
                        e.target.value ? draftFor(meta, e.target.value) : null)}
                    disabled={disabled}
                />
            );
        }

        // STRING / INTEGER / DOUBLE. The unit shares the row with the input
        // when there's room and wraps below it when there isn't (phones),
        // instead of forcing the column wider.
        return (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                <input
                    className="bh-input"
                    type="text"
                    value={(current?.value as string) ?? ""}
                    onChange={(e) => onChange(meta.name,
                        e.target.value.trim() === "" ? null : draftFor(meta, e.target.value))}
                    disabled={disabled}
                    style={{ flex: "1 1 10rem", width: "auto", minWidth: "8rem" }}
                />
                {isDouble && allowedUnits.length > 0 ? (
                    <select
                        className="bh-input"
                        value={current?.units ?? pickDefaultUnit(allowedUnits)}
                        onChange={(e) => {
                            if (current) onChange(meta.name, { ...current, units: e.target.value });
                        }}
                        disabled={disabled || allowedUnits.length === 1}
                        style={{ width: "auto" }}
                    >
                        {allowedUnits.map((uid) => <option key={uid} value={uid}>{unitName(uid)}</option>)}
                    </select>
                ) : meta.unitId ? (
                    <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                        {unitName(meta.unitId)}
                    </span>
                ) : null}
            </div>
        );
    };

    if (parameters.length === 0) return null;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {!hideNote && (
                <small style={{ color: "var(--text-secondary)" }}>
                    All optional — anything left empty is simply not added. You can
                    still add or change parameters after saving.
                </small>
            )}
            {headerExtra}
            {parameters.map((meta) => (
                <div key={meta.name} className="inventory-parameter-row stacked-label">
                    <div className="col name-col">
                        <span className="bh-field">{meta.name}</span>
                        {meta.description && <div className="desc-col">{meta.description}</div>}
                    </div>
                    <div className="col value-col">{rowFor(meta)}</div>
                </div>
            ))}
        </div>
    );
};

export default CreateParametersForm;